import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Stack, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ME } from '~/graphql/queries';
import { ROUTES } from '~/shared/constants';

/**
 * TreeRouteGuard protects the /tree route from access when onboarding is
 * incomplete.
 * It checks if the user's person record has firstName AND lastName.
 * If either is missing, redirects to /onboarding for new user onboarding form
 */
export const TreeRouteGuard = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(ME, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (loading) {
      return;
    }

    // Redirect on query failure
    if (error) {
      navigate(ROUTES.LOGIN);
      return;
    }

    const me = data?.me;
    // Redirect on missing authentication
    if (!me) {
      navigate(ROUTES.LOGIN);
      return;
    }

    // Only check person name completion for users with owned trees
    // Invited users (membership-only) already have their person records created with names
    if (me.ownedTree) {
      const userPerson = me.ownedTree.persons?.find(
        (person) => person?.userId === me.id,
      );

      // If person record exists but lacks first name OR last name, redirect to onboarding
      if (userPerson && (!userPerson.firstName || !userPerson.lastName)) {
        navigate(ROUTES.ONBOARDING.ROOT);
      }
    }
  }, [data, loading, error, navigate]);

  if (loading) {
    return <LoadingContainer direction="row" />;
  }

  // Prevent rendering before navigation takes effect
  if (error || !data?.me) {
    return null;
  }

  return <Outlet />;
};

const LoadingContainer = styled(Stack)({
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.custom.cream,
});
